import React, { useState } from "react";
import { FolderOpen, ExternalLink, Pencil, Info } from "lucide-react";
import { Spinner } from "./ui/kit";
import { apiError } from "../lib/api";
import { infoSluzby, NAPOVEDA_ODKAZU } from "../lib/odkazy";
import KdoUvidi, { PopisViditelnosti } from "./KdoUvidi";

/**
 * Složka s dokumenty na disku firmy (klient nebo obchodní případ).
 * Soubory zůstávají na SharePointu / OneDrivu / Google Disku, ProfitPilot
 * jen otevře složku. `zdedena` = složka klienta, když případ vlastní nemá.
 * `onUlozit({slozka_url, slozka_komu, slozka_ai})` — bez ní jen pro čtení;
 * prázdná `slozka_url` složku odebere. `komu` / `ai` viz KdoUvidi.
 */
export default function SlozkaDokumentu({ url, komu = [], ai = false, zdedena, onUlozit, popisek = "Složka s dokumenty", testid = "slozka" }) {
  const [uprava, setUprava] = useState(false);
  const [hodnota, setHodnota] = useState("");
  const [vybrani, setVybrani] = useState([]);
  const [proAi, setProAi] = useState(false);
  const [uklada, setUklada] = useState(false);
  const [chyba, setChyba] = useState("");
  const [napoveda, setNapoveda] = useState(false);

  const otevritUpravu = () => { setHodnota(url || ""); setVybrani(komu || []); setProAi(!!ai); setChyba(""); setUprava(true); };
  const ulozit = async (nova) => {
    setUklada(true); setChyba("");
    try { await onUlozit({ slozka_url: nova.trim(), slozka_komu: vybrani, slozka_ai: proAi }); setUprava(false); }
    catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
    finally { setUklada(false); }
  };

  if (uprava) {
    return (
      <form className="space-y-2" data-testid={`${testid}-uprava`}
        onSubmit={(e) => { e.preventDefault(); ulozit(hodnota); }}>
        <input className="field" type="url" inputMode="url" autoFocus value={hodnota}
          onChange={(e) => setHodnota(e.target.value)} placeholder="https://firma.sharepoint.com/…"
          aria-label="Odkaz na složku" data-testid={`${testid}-vstup`} />
        <p className="t-meta text-muted">{NAPOVEDA_ODKAZU}</p>
        <KdoUvidi komu={vybrani} onKomu={setVybrani} ai={proAi} onAi={setProAi} testid={`${testid}-kdo`} />
        {chyba && <p className="t-meta text-danger" data-testid={`${testid}-chyba`}>{chyba}</p>}
        <div className="flex flex-wrap items-center gap-2">
          <button type="submit" disabled={uklada || !hodnota.trim()} className="btn-primary tap md:min-h-0" data-testid={`${testid}-ulozit`}>
            {uklada ? <Spinner /> : "Uložit"}
          </button>
          <button type="button" onClick={() => setUprava(false)} className="btn-ghost tap md:min-h-0">Zrušit</button>
          {url && (
            <button type="button" onClick={() => ulozit("")} disabled={uklada} data-testid={`${testid}-odebrat`}
              className="tap md:min-h-0 ml-auto t-meta font-600 text-muted hover:text-danger">Odebrat složku</button>
          )}
        </div>
      </form>
    );
  }

  const cil = url || zdedena?.url;
  if (!cil) {
    if (!onUlozit) return <p className="t-meta text-muted-2">Složka s dokumenty není připojená.</p>;
    return (
      <div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={otevritUpravu} data-testid={`${testid}-pripojit`}
            className="tap flex-1 min-w-0 rounded-[14px] border-[1.5px] border-dashed border-line-strong px-3.5 py-3 text-left hover:border-accent-hover hover:bg-[#fff8f2] flex items-center gap-3">
            <FolderOpen size={19} className="text-muted shrink-0" />
            <span className="min-w-0">
              <span className="block t-telo md:text-[13.5px] font-700 text-ink">Připojit složku s dokumenty</span>
              <span className="block t-meta text-muted">SharePoint, OneDrive nebo Google Disk</span>
            </span>
          </button>
          <button type="button" onClick={() => setNapoveda((x) => !x)} aria-label="Jak to funguje" aria-expanded={napoveda}
            className="tap md:min-h-0 md:min-w-0 h-9 w-9 rounded-full text-muted hover:text-ink inline-flex items-center justify-center shrink-0">
            <Info size={17} />
          </button>
        </div>
        {napoveda && (
          <p className="t-meta text-muted mt-2" data-testid={`${testid}-napoveda`}>
            Soubory zůstávají na disku vaší firmy — ProfitPilot si pamatuje jen odkaz a složku otevře jedním klikem.
            Kdo nemá přístup ke složce, neotevře ji ani z aplikace.
          </p>
        )}
      </div>
    );
  }

  const s = infoSluzby(cil);
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <span className="h-10 w-10 rounded-[12px] inline-flex items-center justify-center shrink-0"
        style={{ background: s.bg, color: s.barva }}><FolderOpen size={19} strokeWidth={1.9} /></span>
      <span className="min-w-0 flex-1">
        <span className="block t-telo md:text-[13.5px] font-700 text-ink truncate" data-testid={`${testid}-sluzba`}>{s.nazev}</span>
        <span className="block t-meta text-muted truncate">
          {url ? popisek : zdedena?.label || "Složka klienta"}
          <PopisViditelnosti komu={url ? komu : zdedena?.komu} ai={url ? ai : zdedena?.ai} />
        </span>
      </span>
      <a href={cil} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-otevrit`}
        className="tap md:min-h-0 pill bg-ink text-white px-3.5 h-10 md:h-9 t-meta font-700 inline-flex items-center gap-1.5 shrink-0 hover:bg-black">
        Otevřít <ExternalLink size={14} />
      </a>
      {onUlozit && (
        <button type="button" onClick={otevritUpravu} aria-label={url ? "Změnit složku" : "Připojit vlastní složku"}
          title={url ? "Změnit složku" : "Připojit vlastní složku"} data-testid={`${testid}-upravit`}
          className="tap md:min-h-0 md:min-w-0 h-9 w-9 rounded-full bg-inputbg inline-flex items-center justify-center text-sec hover:text-ink shrink-0">
          <Pencil size={14} />
        </button>
      )}
    </div>
  );
}
